import { BaseQueryFn, FetchArgs, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { createApi } from "@reduxjs/toolkit/query/react";
import {
  clearStoredAdminUser,
  getStoredAdminToken,
} from "../auth/authSession";
import {
  clearStoredClientUser,
  getStoredClientToken,
} from "../auth/clientSession";
import type { ApiErrorData, ApiValidationErrorItem } from "../types/common";

export const API_BASE_URL =
  typeof window === "undefined"
    ? "https://dialin24api.dialin24.in"
    : `${window.location.protocol}//dialin24api.dialin24.in`;
    
const isClientPath = () => {
  if (typeof window === "undefined") return false;
  return window.location.pathname.toLowerCase().startsWith("/client");
};

const getActiveAuthToken = () => {
  if (typeof window === "undefined") {
    return getStoredAdminToken() || getStoredClientToken();
  }

  // Prefer the token that matches the surface the user is currently on
  // so the admin & client portals don't accidentally hand each other tokens.
  if (isClientPath()) {
    return getStoredClientToken() || getStoredAdminToken();
  }

  return getStoredAdminToken() || getStoredClientToken();
};

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  prepareHeaders: (headers) => {
    // Intentionally NOT setting Content-Type here. fetchBaseQuery sets
    // "application/json" automatically for plain object bodies and leaves
    // it unset for FormData / Blob so the browser can set the correct
    // multipart boundary on file uploads.
    const token = getActiveAuthToken();
    if (token && !headers.has("Authorization")) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    // Helpful for some proxies to identify the call as an AJAX request.
    headers.set("X-Requested-With", "XMLHttpRequest");
    return headers;
  },
});

const getMessagesFromErrors = (errors: unknown): string[] => {
  if (!errors) {
    return [];
  }

  if (typeof errors === "string") {
    return errors.trim() ? [errors] : [];
  }

  if (Array.isArray(errors)) {
    return errors
      .flatMap((item) => {
        if (typeof item === "string") {
          return item;
        }

        if (item && typeof item === "object") {
          const typedItem = item as ApiValidationErrorItem;

          return (
            typedItem.description ?? typedItem.message ?? typedItem.errorMessage ?? ""
          );
        }

        return "";
      })
      .filter((value): value is string => Boolean(value && value.trim()));
  }

  if (typeof errors === "object") {
    return Object.values(errors as Record<string, string[] | string>)
      .flatMap((value) => (Array.isArray(value) ? value : [value]))
      .filter((value): value is string => Boolean(value && String(value).trim()))
      .map((value) => String(value));
  }

  return [];
};

export const getApiErrorMessage = (error: FetchBaseQueryError | undefined) => {
  if (!error) {
    return "Something went wrong. Please try again.";
  }
  if (error.status === "FETCH_ERROR") {
    return `Unable to reach the API at ${API_BASE_URL}. Check that the server is running and that its URL, HTTPS certificate, and CORS settings are correct.`;
  }

  if (error.status === "PARSING_ERROR") {
    return "The API response could not be read correctly.";
  }

  if (typeof error.status === "number") {
    // Friendlier defaults for common auth failures even when the server
    // returns an empty body.
    const data = error.data as ApiErrorData;

    if (typeof data === "string" && data.trim()) {
      return data;
    }

    if (data && typeof data === "object") {
      const errorMessages = getMessagesFromErrors(data.errors);

      if (errorMessages.length > 0) {
        return errorMessages.join(", ");
      }

      if (data.message) {
        return data.message;
      }

      if (data.title) {
        return data.title;
      }

      if (data.error) {
        return data.error;
      }
    }

    if (error.status === 401) {
      return "Your session has expired. Please sign in again.";
    }
    if (error.status === 403) {
      return "You do not have permission to perform this action.";
    }
    if (error.status === 404) {
      return "The requested resource was not found.";
    }
    if (error.status >= 500) {
      return "The server ran into an error. Please try again shortly.";
    }
  }

  return "Something went wrong. Please try again.";
};

const isFalseLike = (value: unknown) =>
  value === false || value === "false" || value === 0 || value === "0";

const ADMIN_LOGIN_PATH = "/admin";
const CLIENT_LOGIN_PATH = "/client/login";

const clearSessionForCurrentSurface = () => {
  if (typeof window === "undefined") {
    return;
  }

  if (isClientPath()) {
    clearStoredClientUser();
  } else {
    clearStoredAdminUser();
  }
};

const redirectToLogin = () => {
  if (typeof window === "undefined") {
    return;
  }

  const path = window.location.pathname;
  // Don't bounce while the user is already on the login screen — that just
  // produces a flicker without changing anything.
  if (isClientPath()) {
    if (!path.startsWith(CLIENT_LOGIN_PATH)) {
      window.location.assign(CLIENT_LOGIN_PATH);
    }
    return;
  }

  // The admin shell renders its own login modal whenever the admin user is
  // missing, so just sending it back to /admin is enough to trigger that.
  if (!path.startsWith(ADMIN_LOGIN_PATH)) {
    window.location.assign(ADMIN_LOGIN_PATH);
  }
};

const isAuthEndpoint = (args: string | FetchArgs) => {
  const url = typeof args === "string" ? args : args.url || "";
  return /\/auth\/(login|register|logout)/i.test(url);
};

const baseQueryWithSimpleErrorHandling: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions);

  // Global 401: token expired or missing — clear session and bounce.
  if (
    result.error &&
    typeof result.error.status === "number" &&
    result.error.status === 401 &&
    !isAuthEndpoint(args)
  ) {
    clearSessionForCurrentSurface();
    redirectToLogin();
    return result;
  }

  if (result.error) {
    return result;
  }

  const data = result.data as ApiErrorData;
  if (data && typeof data === "object") {
    const hasErrors = getMessagesFromErrors(data.errors).length > 0;
    const isSuccessFalse = data.success === false;
    const isStatusFalse = isFalseLike(data.status);
    const isStatusCodeFalse = isFalseLike(data.statusCode);
    if (hasErrors || isSuccessFalse || isStatusFalse || isStatusCodeFalse) {
      return {
        error: {
          status: 400,
          data,
        },
      };
    }
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: "baseApi",
  baseQuery: baseQueryWithSimpleErrorHandling,
  keepUnusedDataFor: 300,
  tagTypes: [
    "AdminAuth",
    "ClientAuth",
    "Request",
    "Project",
    "Review",
    "Employee",
    "ProjectImage",
    "AdminProfile",
    "Client",
    "ClientPermission",
    "Expense",
    "Payment",
  ],
  endpoints: () => ({}),
});
